import { FC, HTMLAttributes, useEffect, useRef } from "react";
import clsx from "classnames";
import { useStyles } from "./Dialog.style";
import Button, { IButtonProps } from "../Button/Button";
import { Typography } from "../Typography/Typography";
import { TypographyVariant } from "../../../constants/typography";

interface IButtonAction {
  text: string | React.ReactNode;
  icon?: React.HTMLAttributes<HTMLDivElement>;
  loading?: boolean;
}

interface IDialogProps {
  open: boolean;
  mainAction?: IButtonAction & IButtonProps & HTMLAttributes<HTMLDivElement>;
  secondAction?: IButtonAction & IButtonProps & HTMLAttributes<HTMLDivElement>;
  onClose?: () => void;
  stickBottom?: boolean;
  enableScrollBackground?: boolean;
}

export const Dialog: FC<IDialogProps & HTMLAttributes<HTMLDialogElement>> = ({
  open = false,
  mainAction,
  secondAction,
  children,
  ...otherProps
}) => {
  const classes = useStyles();
  const ref = useRef<HTMLDialogElement>(null);

  const closeModal = () => {
    ref.current?.close?.();
  };

  const showModal = () => {
    ref.current?.showModal?.();
  };

  const lockScroll = () => {
    document.body.style.overflow = "hidden";
    document.body.style.width = "100vw";
    document.body.style.height = "100vh";
  };

  const unlockScroll = () => {
    document.body.style.removeProperty("overflow");
    document.body.style.removeProperty("position");
    document.body.style.removeProperty("width");
    document.body.style.removeProperty("height");
  };

  useEffect(() => {
    if (!ref.current) return;
    if (open) {
      lockScroll();
      showModal();
    } else {
      unlockScroll();
      closeModal();
    }
    return () => {
      unlockScroll();
    };
  }, [open, ref]);

  if (!open) {
    return null;
  }

  return (
    <dialog {...otherProps} ref={ref} className={clsx(classes.root, { open })}>
      <div className={clsx(classes.content, otherProps.className)}>
        {children}
      </div>
      <div className={classes.actions}>
        {secondAction && (
          <Button
            variant="outlined"
            color="secondary"
            fullWidth
            loading={secondAction?.loading}
            {...secondAction}
            data-testid="accept-action"
          >
            {secondAction.text}
          </Button>
        )}
        {mainAction && (
          <Button
            variant="contained"
            fullWidth
            loading={mainAction?.loading}
            {...mainAction}
            data-testid="cancel-action"
          >
            <Typography lines={1} variant={TypographyVariant.BodyB3Bold}>
              {mainAction?.text || "Ok"}
            </Typography>
          </Button>
        )}
      </div>
    </dialog>
  );
};
